'use client';
import { MapContext, useResizeObserver } from '@iib/pandora-box';
import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import {
  DEFAULT_ZOOM_MAP,
  ISPRINTMAPALLOWED,
  MTK_API_KEY,
} from '../../../utils/constants';
import {
  MAP_ID,
  MAP_ATTRIBUTION,
  MAX_ZOOM,
  MIN_ZOOM,
  PITCH,
  BEARING,
} from '../../../utils/map.constants';
import * as S from './styles/index.styled';
import { LngLatLike } from 'mapbox-gl';
import Legend from './Legend/Legend';
import POILegend from './POILegend/POILegend';
import { TestID } from '../../../utils/testId';
import {
  selectPOIsBasedOnSelectedOption,
  addPOILayersToMap,
} from '../../../utils/targomoUtils';

export enum SelectedLayer {
  Wohnlage = 'Wohnlage',
  Kultur = 'Kultur',
  Oepnv = 'ÖPNV',
  Nahversorgung = 'Nahversorgung',
}

enum MapType {
  Wohnlagen = 'wohnlagen',
  Lagescores = 'iib-iib_lagescores_zf5l',
}

type MapProps = {
  mapId?: string;
  isTest?: boolean;
  center?: {
    lat: number;
    lng: number;
  };
  selectedLayer?: SelectedLayer;
  mapType?: MapType;
  height?: string;
  width?: string;
  isShowLegend?: boolean;
  isShowPOILegend?: boolean;
  goToLocation?: [number, number];
  zoom?: number;
  allowScreenCapture?: boolean;
};

const convertSelectedLayer = (layer: SelectedLayer): string => {
  switch (layer) {
    case SelectedLayer.Wohnlage:
      return 'wohnlagen';
    case SelectedLayer.Kultur:
      return 'culture';
    case SelectedLayer.Oepnv:
      return 'oepnv';
    case SelectedLayer.Nahversorgung:
      return 'localsupply';
    default:
      return '';
  }
};

export const Map: FC<MapProps> = ({
  mapId = MAP_ID,
  isTest = false,
  center,
  selectedLayer,
  mapType = MapType.Wohnlagen,
  height: propHeight,
  width: propWidth,
  isShowLegend = false,
  isShowPOILegend = false,
  goToLocation,
  zoom,
  allowScreenCapture = ISPRINTMAPALLOWED === 'true' ? true : false,
}) => {
  const container = useRef<HTMLDivElement>(null);
  const [initialRenderComplete, setInitialRenderComplete] =
    useState<boolean>(isTest);
  const [height, setHeight] = useState<string>(propHeight || '100%');
  const [width, setWidth] = useState<string>('100%');
  const [effectiveMapType, setEffectiveMapType] = useState<MapType>(mapType);
  const [previousLayer, setPreviousLayer] = useState<SelectedLayer | null>(
    null,
  );
  const [markerIds, setMarkerIds] = useState<string[]>([]);

  const dimensions = useResizeObserver(container);
  const {
    map: mapDictionary,
    waitForMapToLoad,
    setMap,
    addMarker,
    removeMarker,
    addSource,
    addLayer,
  } = useContext(MapContext);

  const updateLayerVisibility = (
    map: maplibregl.Map,
    layerId: string,
    buildingId: string,
    visibility: 'visible' | 'none',
  ) => {
    if (map.getLayer(layerId) && map.getLayer(buildingId)) {
      map.setLayoutProperty(buildingId, 'visibility', visibility);
      map.setLayoutProperty(layerId, 'visibility', visibility);
    }
  };

  useEffect(() => {
    if (!selectedLayer || !initialRenderComplete) return;

    const mtkMap = mapDictionary?.[mapId];
    const map = mtkMap?.gl as maplibregl.Map;
    if (!map) return;

    const convertedLayer = convertSelectedLayer(selectedLayer);
    const layerId = `lagescores_${convertedLayer}`;
    const buildingId = `building_${convertedLayer}`;

    if (previousLayer) {
      const prevConvertedLayer = convertSelectedLayer(previousLayer);
      const prevLayerId = `lagescores_${prevConvertedLayer}`;
      const prevBuildingId = `building_${prevConvertedLayer}`;
      updateLayerVisibility(map, prevLayerId, prevBuildingId, 'none');
    }

    const handleMapTypeChange = () => {
      if (map.getLayer(layerId) && map.getLayer(buildingId)) {
        map.setLayoutProperty(layerId, 'visibility', 'visible');
        map.setLayoutProperty(buildingId, 'visibility', 'visible');
      }
    };

    mtkMap?.on('maptypechanged', handleMapTypeChange);

    updateLayerVisibility(map, layerId, buildingId, 'visible');

    setPreviousLayer(selectedLayer);

    return () => {
      mtkMap?.off('maptypechanged', handleMapTypeChange);
    };
  }, [selectedLayer, initialRenderComplete, mapDictionary]);

  useEffect(() => {
    const updatedMapType =
      selectedLayer === SelectedLayer.Wohnlage
        ? MapType.Wohnlagen
        : MapType.Lagescores;
    setEffectiveMapType(updatedMapType);

    const map = mapDictionary?.[mapId]?.gl as maplibregl.Map;
    if (map && selectedLayer) {
      map.setStyle(updatedMapType);
      map.once('styledata', () => {
        const selectedPOIs = selectPOIsBasedOnSelectedOption(selectedLayer);
        addPOILayersToMap(selectedPOIs, map, addSource, addLayer);
      });
    }
  }, [selectedLayer]);

  const updateMapInHook = async () => {
    const loaded = await waitForMapToLoad?.(mapId);
    if (!loaded || !mapDictionary?.[mapId]) return;
    setMap?.(mapId, mapDictionary?.[mapId]);
  };

  useEffect(() => {
    if (!initialRenderComplete) return;
    updateMapInHook();
  }, [initialRenderComplete, mapDictionary?.[mapId]]);

  useEffect(() => {
    setInitialRenderComplete(true);
  }, []);

  useEffect(() => {
    if (
      !dimensions?.width ||
      !dimensions?.height ||
      dimensions?.height === 0 ||
      !initialRenderComplete ||
      propHeight ||
      propWidth
    )
      return;

    setHeight(`${dimensions.height}px`);
    setWidth(`${dimensions.width}px`);

    const wohnpreisMap = mapDictionary?.[mapId];
    if (wohnpreisMap) {
      wohnpreisMap.gl.resize();
    }
  }, [dimensions, initialRenderComplete, mapDictionary, propHeight, propWidth]);

  useEffect(() => {
    if (!goToLocation || goToLocation.length !== 2 || !initialRenderComplete)
      return;

    const mtkMap = mapDictionary?.[mapId];
    const map = mtkMap?.gl as maplibregl.Map;
    if (!map) return;

    markerIds.forEach((id) => {
      removeMarker?.(id);
    });

    const newMarkerId = 'marker-id-new';
    const element = {
      location: goToLocation as LngLatLike,
      id: newMarkerId,
    };
    addMarker?.(mapId, element);
    setMarkerIds([newMarkerId]);

    map.flyTo({
      center: goToLocation,
      zoom: 17,
    });
  }, [goToLocation, initialRenderComplete, mapDictionary]);

  useEffect(() => {
    const mtkMap = mapDictionary?.[mapId];
    const map = mtkMap?.gl as maplibregl.Map;
    if (!map) return;

    if (center) {
      map.setCenter(center as LngLatLike);
    }
  }, [center]);

  return (
    <S.MapWrapper
      ref={container}
      style={{
        height: propHeight || height,
        width: propWidth || width,
        position: 'relative',
      }}
    >
      {height && width && (
        <S.MapMTK
          data-testid={TestID.WohnpreisMap}
          mapId={mapId}
          mapType={effectiveMapType}
          apiKey={MTK_API_KEY}
          initialViewState={{
            center: [
              center?.lng as keyof LngLatLike,
              center?.lat as keyof LngLatLike,
            ],
            zoom: zoom ?? DEFAULT_ZOOM_MAP,
            maxZoom: MAX_ZOOM,
            minZoom: MIN_ZOOM,
            pitch: PITCH,
            bearing: BEARING,
          }}
          attribution={{ customAttribution: MAP_ATTRIBUTION, compact: true }}
          style={{ height: propHeight || height, width }}
          allowScreenCapture={allowScreenCapture}
        />
      )}
      {isShowLegend && <Legend selectedOption={selectedLayer} />}
      {isShowPOILegend && <POILegend selectedOption={selectedLayer} />}
    </S.MapWrapper>
  );
};

export default Map;
